import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "../components/Sidebar";
import { useEffect, useMemo, useState } from "react";
import { getUser } from "../services/utils";
import { UserProvider, useUser } from "../services/user-context";
import { PraxisCallProvider } from "./praxis-play/context/praxiscall-context";
import { MobileHeaderMenu } from "../components/Sidebar/MobileSidebar";
import { ReactComponent as PraxisProIcon } from "../assets/icons/white-logo.svg";
import { ReactComponent as CloseIcon } from "../assets/icons/x.svg";
import { ReactComponent as HamburgerIcon } from "../assets/icons/hamburger.svg";
import { EditProfileModal } from "./auth/profile/EditProfile";
import { useApp } from "../services/app-context";
import { CertificationProvider } from "./certifications/context/certification-context";
import { ReportsProvider } from "./praxis-play-dashboard/context/reports-context";
import { isBadgeCenterEnabled, isChatbotEnabled, isNotificationEnabled, isSSOEnabled } from "../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../integrations/posthog/usePostHogFeatureFlag";
import { Onboarding } from "./onboarding/Onboarding";
import { Notification } from "./notification/Notification";
import { Chatbot } from "./chatbot/Chatbot";
import { ComplianceAcknowledgement } from "./ComplianceAcknowledgement";
import { TutorialProvider } from "../contexts/TutorialContext";
import { OnboardingProvider, useOnboarding } from "../contexts/OnboardingContext";
import { tutorialSteps } from "./praxis-play-dashboard/tutorial-config";
import { AwardPopup } from "./badge-center/notification-popups/AwardPopup";

function MainApp() {
  const user = getUser();
  const { userInfo, loading, questionnaireCompleted } = useUser() as any;
  const { logout, tenantProfile } = useApp();
  const { showOnboarding, setShowOnboarding } = useOnboarding();
  const [expand, setExpand] = useState(false);
  const [edit, setEdit] = useState(false);
  const [showComplianceDialog, setShowComplianceDialog] = useState(() => {
    // Check if already acknowledged in this session
    const isAcknowledged = sessionStorage.getItem("complianceAcknowledged");
    return !isAcknowledged; // show only if not acknowledged
  });

  const complianceDialog = tenantProfile?.compliance_acknowledgement_modal

  const handleLogout = async () => {
    // Stays in the app, so a rejection needs no recovery UI — the logout toast
    // already covered it. (/signout catches it separately to offer a way back.)
    void logout().catch(() => {});
  };

  const onAcceptCompliance = () => {
    sessionStorage.setItem("complianceAcknowledged", "true");
    setShowComplianceDialog(false);
  };

  const handleEdit = () => {
    setEdit(true);
  };

  const shouldShowSidebar = useMemo(() => {
    // hide if it's /signout page
    if (window.location.pathname === "/signout") {
      return false;
    }
    return true;
  }, [window.location.pathname]);

  const onboardingSessionKey = userInfo?.id
    ? `praxisOnboardingShown:${userInfo.id}`
    : null;

  // Dismiss/complete the questionnaire modal and record that it was shown for this
  // tab session. The session flag is written HERE (on dismissal/completion), not when
  // the modal opens, on purpose: a Cognito token refresh calls fetchUser(), which flips
  // userInfoLoading and makes UserProvider unmount/remount the whole app tree, resetting
  // OnboardingProvider state (showOnboarding → false). If the flag were written at open
  // time, that remount would leave the flag set while showOnboarding was reset, so the
  // effect below would refuse to re-open an in-progress questionnaire and it would vanish
  // for the rest of the tab session. Writing the flag only once the user is actually done
  // with the modal means a mid-questionnaire remount re-opens it instead of dropping it.
  const dismissOnboarding = () => {
    if (onboardingSessionKey) {
      sessionStorage.setItem(onboardingSessionKey, "true");
    }
    setShowOnboarding(false);
  };

  // Show the questionnaire modal at most once per app load, and only when the
  // questionnaire has not been completed yet. questionnaireCompleted is derived in
  // UserContext (single source of truth). The tooltip tutorial appears on its own
  // once the questionnaire is done and the tooltip is not completed.
  //
  // We must NOT re-open the modal on every render/navigation. The per-user, per-session
  // flag in sessionStorage (cleared when the tab closes, written by dismissOnboarding)
  // makes "show once per app load" survive remounts (token refresh) and route changes
  // without re-opening on top of whatever screen the user is on (e.g. PraxisPlay Insights).
  useEffect(() => {
    if (!userInfo?.id || loading) {
      return;
    }

    // While the compliance dialog is blocking render, Onboarding cannot appear
    // (the early return below wins). Don't flip showOnboarding here: a remount
    // (token refresh) before compliance is accepted resets OnboardingProvider state.
    if (showComplianceDialog && complianceDialog?.show) {
      return;
    }

    // Completed → ensure it is closed and never shown again.
    if (questionnaireCompleted) {
      setShowOnboarding(false);
      return;
    }

    // Not completed and not yet dismissed for this session → show it. Because the
    // flag is only written on dismissal/completion, a remount that happens while the
    // modal is still open finds the flag unset and re-opens it, so an in-progress
    // questionnaire is never silently dropped.
    if (onboardingSessionKey && sessionStorage.getItem(onboardingSessionKey)) {
      return;
    }

    setShowOnboarding(true);
  }, [questionnaireCompleted, loading, userInfo?.id, onboardingSessionKey, setShowOnboarding, showComplianceDialog, complianceDialog?.show]);

  if (showComplianceDialog && complianceDialog?.show) {
    return <ComplianceAcknowledgement onAcceptCompliance={onAcceptCompliance} complianceText={complianceDialog?.text} title={complianceDialog?.title} />;
  }

  return (
    <>
      {showOnboarding && <Onboarding onClose={dismissOnboarding} />}
      <div className="flex-1 flex flex-col lg:flex-row">
        {shouldShowSidebar && (
          <Sidebar
            user={{ ...user, ...userInfo }}
            onEdit={handleEdit}
            userInfoLoading={loading}
          />
        )}
        <div className="fixed lg:hidden z-[5] top-0 left-0 right-0 px-4 lg:pl-10 lg:pr-5 py-[18px] bg-PraxisDarkRed border-gray-200 border-b border-opacity-10 backdrop-blur-3xl justify-between items-center flex">
          <PraxisProIcon />
          <div className="justify-end items-center gap-3 flex flex-1">
            <button
              type="button"
              className="lg:hidden"
              onClick={() => setExpand(!expand)}
            >
              {expand ? (
                <CloseIcon className="h-8 w-8 [&_path]:stroke-white" />
              ) : (
                <HamburgerIcon className="h-8 w-8 [&_path]:stroke-white" />
              )}
            </button>
          </div>
        </div>
        <div className="lg:hidden">
          {expand && (
            <MobileHeaderMenu
              onClose={() => setExpand(false)}
              user={{ ...user, ...userInfo }}
              onLogout={handleLogout}
              userInfoLoading={loading}
              onEdit={() => {
                // setOpenDropdown(false);
                // setIsProfileOpen(true);
                setEdit(true);
              }}
            />
          )}
        </div>
        <div className="flex-1 max-h-screen overflow-auto bg-background">
          <Outlet />
        </div>
        {edit && (
          <EditProfileModal isOpen={edit} onClose={() => setEdit(false)} />
        )}
      </div>
    </>
  );
}

export function Main() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const { userSession, userSessionLoading, tenantLoading, tenantProfile, notificationModal, setNotificationModal } = useApp();
  const isBadgeCenterFlagOn = usePostHogFeatureFlag(isBadgeCenterEnabled);

  useEffect(() => {
    if (!tenantProfile?.sso_redirect_url || !isSSOEnabled()) {
      if (userSessionLoading) return;
      if (tenantLoading) return;
      if (!userSession) {
        navigate("/login");
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(true);
        if (location.pathname === "/") {
          navigate("/dashboard");
        }
      }
    } else {
      // check for id_token in storage
      const idToken = localStorage.getItem("id_token");
      if (!idToken) {
        navigate("/login");
        setIsLoggedIn(false);
      } else {
        setIsLoggedIn(true);
        if (location.pathname === "/") {
          navigate("/dashboard");
        }
      }
    }
  }, [userSession, userSessionLoading, tenantLoading, tenantProfile]);

  if (!isLoggedIn) return null;

  return (
    <UserProvider>
      <OnboardingProvider>
        <TutorialProvider totalSteps={tutorialSteps.length} steps={tutorialSteps} autoStart={true}>
          <PraxisCallProvider>
            <ReportsProvider>
              <MainApp />
            </ReportsProvider>
          </PraxisCallProvider>
          {notificationModal && isNotificationEnabled() && <Notification onClose={() => setNotificationModal(false)} />}
          {isChatbotEnabled() && <Chatbot />}
          {/** Badge Center Notification Popup only for praxis play */}
          {isBadgeCenterFlagOn && <AwardPopup />}
        </TutorialProvider>
      </OnboardingProvider>
    </UserProvider>
  );
}